import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  AcsUserListItem, AcsUserDetail, AcsTradeUnitDefault, AcsBillAddress,
  AcsLocationNote, AcsElevatorLocation, AcsCtrmElevator, AcsCustomer,
  AcsCommLoadWtDefault, AcsCommodity, AcsTradeUnitCommodity, AcsLoc, AcsGroupListItem,
  AcsGroupDetail, AcsDirectoryPerson, AcsLocAssignment, AcsReferenceData, AcsScopedLocs
} from '../admin-config/models/acs.models';

@Injectable({
  providedIn: 'root'
})
export class AdminConfigService {

  private readonly baseUrl: string = 'api/AdminConfig';

  constructor(private http: HttpClient) {}

  // Reference Data
  getReferenceData(): Observable<AcsReferenceData> {
    return this.http.get<AcsReferenceData>(`${this.baseUrl}/reference-data`);
  }

  // User Management
  getAllActiveLocs(): Observable<AcsLocAssignment[]> {
    return this.http.get<AcsLocAssignment[]>(`${this.baseUrl}/all-locs`);
  }

  getUsers(nameFilter: string = '', officeFilter: string = ''): Observable<AcsUserListItem[]> {
    let params = new HttpParams();
    if (nameFilter) params = params.set('nameFilter', nameFilter);
    if (officeFilter) params = params.set('officeFilter', officeFilter);
    return this.http.get<AcsUserListItem[]>(`${this.baseUrl}/users`, { params });
  }

  getUserById(userId: number): Observable<AcsUserDetail> {
    return this.http.get<AcsUserDetail>(`${this.baseUrl}/users/${userId}`);
  }

  saveUser(user: AcsUserListItem): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseUrl}/users/save`, user);
  }

  saveUserLocs(userId: number, locCodes: string[]): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseUrl}/users/${userId}/locs`, locCodes);
  }

  saveUserGroups(userId: number, groupIds: number[]): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseUrl}/users/${userId}/groups`, groupIds);
  }

  deleteUser(userId: number): Observable<boolean> {
    return this.http.delete<boolean>(`${this.baseUrl}/users/${userId}`);
  }

  searchDirectory(query: string): Observable<AcsDirectoryPerson[]> {
    const params = new HttpParams().set('query', query);
    return this.http.get<AcsDirectoryPerson[]>(`${this.baseUrl}/users/search-directory`, { params });
  }

  // Trade Unit Defaults
  getTradeUnitDefaults(locCode: string = '', commodityCode: string = ''): Observable<AcsTradeUnitDefault[]> {
    let params = new HttpParams();
    if (locCode) params = params.set('locCode', locCode);
    if (commodityCode) params = params.set('commodityCode', commodityCode);
    return this.http.get<AcsTradeUnitDefault[]>(`${this.baseUrl}/trade-unit-defaults`, { params });
  }

  saveTradeUnitDefault(item: Partial<AcsTradeUnitDefault>): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseUrl}/trade-unit-defaults/save`, item);
  }

  deleteTradeUnitDefault(id: number): Observable<boolean> {
    return this.http.delete<boolean>(`${this.baseUrl}/trade-unit-defaults/${id}`);
  }

  // Location Notes
  getLocationNotes(locCode: string = '', locationFilter: string = '', cityFilter: string = '', stateFilter: string = ''): Observable<AcsLocationNote[]> {
    let params = new HttpParams();
    if (locCode) params = params.set('locCode', locCode);
    if (locationFilter) params = params.set('locationFilter', locationFilter);
    if (cityFilter) params = params.set('cityFilter', cityFilter);
    if (stateFilter) params = params.set('stateFilter', stateFilter);
    return this.http.get<AcsLocationNote[]>(`${this.baseUrl}/location-notes`, { params });
  }

  saveLocationNote(note: Partial<AcsLocationNote>): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseUrl}/location-notes/save`, note);
  }

  deleteLocationNote(id: number): Observable<boolean> {
    return this.http.delete<boolean>(`${this.baseUrl}/location-notes/${id}`);
  }

  // Elevator Match
  getElevatorLocations(locCode: string = '', locationFilter: string = '', cityFilter: string = ''): Observable<AcsElevatorLocation[]> {
    let params = new HttpParams();
    if (locCode) params = params.set('locCode', locCode);
    if (locationFilter) params = params.set('locationFilter', locationFilter);
    if (cityFilter) params = params.set('cityFilter', cityFilter);
    return this.http.get<AcsElevatorLocation[]>(`${this.baseUrl}/elevator-locations`, { params });
  }

  getCtrmElevators(locCode: string = ''): Observable<AcsCtrmElevator[]> {
    let params = new HttpParams();
    if (locCode) params = params.set('locCode', locCode);
    return this.http.get<AcsCtrmElevator[]>(`${this.baseUrl}/elevator-locations/ctrm-elevators`, { params });
  }

  saveElevatorLocation(mapping: Partial<AcsElevatorLocation>): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseUrl}/elevator-locations/save`, mapping);
  }

  deleteElevatorLocation(id: number): Observable<boolean> {
    return this.http.delete<boolean>(`${this.baseUrl}/elevator-locations/${id}`);
  }

  // Commodities (all)
  getAllCommodities(): Observable<AcsCommodity[]> {
    return this.http.get<AcsCommodity[]>(`${this.baseUrl}/commodities`);
  }

  // Bill Address (TradeUnitDefaults filtered by Key='BillingAddress')
  getBillAddresses(locCode: string = '', commodityCode: string = ''): Observable<AcsBillAddress[]> {
    let params = new HttpParams();
    if (locCode) params = params.set('locCode', locCode);
    if (commodityCode) params = params.set('commodityCode', commodityCode);
    return this.http.get<AcsBillAddress[]>(`${this.baseUrl}/bill-addresses`, { params });
  }

  // Customer
  searchCustomers(searchTerm: string = ''): Observable<AcsCustomer[]> {
    let params = new HttpParams();
    if (searchTerm) params = params.set('searchTerm', searchTerm);
    return this.http.get<AcsCustomer[]>(`${this.baseUrl}/customers/search`, { params });
  }

  deleteCustomer(customerId: number): Observable<boolean> {
    return this.http.delete<boolean>(`${this.baseUrl}/customers/${customerId}`);
  }

  // Variable Weight
  getCommLoadWtDefaults(locCode: string = ''): Observable<AcsCommLoadWtDefault[]> {
    let params = new HttpParams();
    if (locCode) params = params.set('locCode', locCode);
    return this.http.get<AcsCommLoadWtDefault[]>(`${this.baseUrl}/comm-load-wt-defaults`, { params });
  }

  saveCommLoadWtDefault(item: Partial<AcsCommLoadWtDefault>): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseUrl}/comm-load-wt-defaults/save`, item);
  }

  deleteCommLoadWtDefault(id: number): Observable<boolean> {
    return this.http.delete<boolean>(`${this.baseUrl}/comm-load-wt-defaults/${id}`);
  }

  // Trade Unit Management
  getLocs(): Observable<AcsLoc[]> {
    return this.http.get<AcsLoc[]>(`${this.baseUrl}/locs`);
  }

  saveLoc(loc: Partial<AcsLoc>): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseUrl}/locs/save`, loc);
  }

  deleteLoc(locCode: string): Observable<boolean> {
    return this.http.delete<boolean>(`${this.baseUrl}/locs/${locCode}`);
  }

  // Trade Unit Groups
  getGroups(): Observable<AcsGroupListItem[]> {
    return this.http.get<AcsGroupListItem[]>(`${this.baseUrl}/groups`);
  }

  getGroupById(groupId: number): Observable<AcsGroupDetail> {
    return this.http.get<AcsGroupDetail>(`${this.baseUrl}/groups/${groupId}`);
  }

  saveGroup(group: Partial<AcsGroupListItem>): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseUrl}/groups/save`, group);
  }

  deleteGroup(groupId: number): Observable<boolean> {
    return this.http.delete<boolean>(`${this.baseUrl}/groups/${groupId}`);
  }

  // Trade Unit Commodity Configuration
  getTradeUnitCommodities(locCode: string): Observable<AcsTradeUnitCommodity[]> {
    const params = new HttpParams().set('locCode', locCode);
    return this.http.get<AcsTradeUnitCommodity[]>(`${this.baseUrl}/trade-unit-commodities`, { params });
  }

  saveTradeUnitCommodities(locCode: string, commodityCodes: string[]): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseUrl}/trade-unit-commodities/save`, { locCode, commodityCodes });
  }

  // Scoped Data
  getScopedLocs(): Observable<AcsScopedLocs> {
    return this.http.get<AcsScopedLocs>(`${this.baseUrl}/scoped-locs`);
  }
}
